/**
 * @file tags.jsx
 * @location src/components/ui/sharedui/tags.jsx
 * @role 인라인 「태그」 원자 컴포넌트 — Chk / StatusTag / DueText / AssigneeChip
 *
 * 모든 컴포넌트가 memo — 같은 props 면 리렌더 차단.
 * 다수 리스트 (TaskListSection, TeamTaskTable 등) 안에서 자주 쓰여 효과 큼.
 */
import { memo, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon.jsx';
import { isClosed } from '../../../data/constants';
import { getTodayStr, fmtShort, ddayLabel } from '../../../utils/dateUtils';

/** 체크박스 — 완료 토글 */
export const Chk = memo(function Chk({ done, onClick }) {
  return (
    <button className={`chk${done ? ' dn' : ''}`} onClick={onClick}>
      {done ? <Icon name="check" size={14} /> : ''}
    </button>
  );
});

/**
 * status 가 「변할 때만」 한 번 bump 클래스 발화 (마운트·리오더 시엔 false).
 * 핸드셰이크 단계 전환(읽음→완료)에 살아있는 피드백을 주는 용도.
 * reduced-motion 은 CSS 전역 가드가 즉시화하므로 여기선 무조건 토글.
 */
function useStatusBump(status) {
  const prev = useRef(status);
  const [bump, setBump] = useState(false);
  useEffect(() => {
    if (prev.current === status) return;
    prev.current = status;
    setBump(true);
    const id = setTimeout(() => setBump(false), 450);
    return () => clearTimeout(id);
  }, [status]);
  return bump;
}

/** 상태 태그 — razor ㉓: 진행중 토글 폐기 → 항상 평문 뱃지(onToggle 미사용) */
export const StatusTag = memo(function StatusTag({ status, cls, label }) {
  const bumpCls = useStatusBump(status) ? ' tag-bump' : '';
  return <span className={`tag ${cls}${bumpCls}`} aria-label={`상태: ${label}`}>{label}</span>;
});

/**
 * 기한 셀 — 「오늘 기준」이라 절대 날짜 대신 D-day(N일 남음/오늘/N일 초과) 표시.
 *   종류 마커: 지정(그날-)=📌pin / 기한(그날까지)=⏳pending. 아이콘+라벨로 대칭 구분.
 *   종결(완료·중지)은 D-day 무의미 → 절대 날짜만 차분히. 절대 날짜는 hover(title)로 보존.
 */
export const DueText = memo(function DueText({ due, status, dateKind }) {
  const done = isClosed(status);
  const ondate = dateKind === 'ondate';
  const kind = ondate
    ? { icon: 'pin', label: '지정' }       // 그 날에
    : { icon: 'pending', label: '기한' };  // 그 날까지

  if (!due) return <span className="tdate none">—</span>;
  if (done) {
    return (
      <span className="tdate done" title={fmtShort(due)}>
        <Icon name={kind.icon} size={12} />
        {fmtShort(due)}
      </span>
    );
  }

  const { label, tone } = ddayLabel(due, getTodayStr());
  // 미래 지정일 = 그날 도래 전이라 카운트다운 대신 「예정」 (그 날 오면 today 흐름에서 오늘 표시)
  const isYotei = ondate && tone === 'future';
  return (
    <span className={`tdate dday ${tone}`} title={fmtShort(due)}>
      <Icon name={kind.icon} size={12} />
      {!isYotei && <span className="due-kind">{kind.label}</span>}
      <span className={`dday-val ${tone}`}>{isYotei ? '예정' : label}</span>
    </span>
  );
});

